import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { iv } from './invoiceTheme';

export const RISK_PAGE_SIZE = 12;

export const riskShellSx = {
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75
};

export const riskTabsSx = {
  minHeight: 38,
  flexShrink: 0,
  borderBottom: `1px solid ${iv.border}`,
  '& .MuiTab-root': {
    minHeight: 38,
    py: 0.5,
    px: 1.5,
    fontWeight: 800,
    fontSize: 13,
    textTransform: 'none',
    color: iv.textMuted
  },
  '& .Mui-selected': { color: iv.text },
  '& .MuiTabs-indicator': { height: 2.5, bgcolor: iv.primary }
};

export const riskTableWrapSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#fff',
  border: `1px solid ${iv.border}`,
  borderRadius: iv.radius
};

export const riskTableHeadCellSx = {
  fontWeight: 800,
  fontSize: 11.5,
  color: iv.textMuted,
  bgcolor: '#f8fafc',
  py: 0.75,
  borderBottom: `1px solid ${iv.border}`,
  whiteSpace: 'nowrap'
};

export const riskTableCellSx = {
  fontSize: 12.5,
  py: 0.65,
  borderBottom: `1px solid ${iv.border}`
};

/** Kompakt KPI şeridi — özet sayı + tıklayınca ilgili sekme (opsiyonel filtre) */
export function RiskKpiStrip({ items, onSelect, activeTab, activeFilter }) {
  return (
    <Box
      className="iv-risk-kpi-strip"
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(items.length, 6)}, minmax(0, 1fr))`,
        gap: 0.6,
        flexShrink: 0
      }}
    >
      {items.map((k) => {
        const sameTab = k.tab != null && activeTab === k.tab;
        const filterMatch =
          k.filter == null ? activeFilter == null || activeFilter === false : Boolean(activeFilter) === Boolean(k.filter);
        const active = sameTab && (k.filter == null ? !activeFilter : filterMatch);
        return (
          <Box
            key={k.label}
            onClick={k.tab != null && onSelect ? () => onSelect(k.tab, { filter: k.filter || null }) : undefined}
            title={k.hint || undefined}
            sx={{
              border: `1px solid ${active ? iv.primary : iv.border}`,
              borderRadius: 1.25,
              bgcolor: active ? '#f0f9ff' : '#fff',
              px: 1,
              py: 0.55,
              borderLeft: `3px solid ${k.color || iv.primary}`,
              cursor: k.tab != null ? 'pointer' : 'default',
              minWidth: 0,
              '&:hover': k.tab != null ? { bgcolor: active ? '#e0f2fe' : '#f8fafc' } : undefined
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 800,
                color: iv.textMuted,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {k.label}
            </Typography>
            <Typography
              sx={{
                fontSize: k.small ? 12 : 18,
                fontWeight: 900,
                color: k.color || iv.text,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {k.value}
            </Typography>
            {k.hint ? (
              <Typography
                sx={{
                  fontSize: 10,
                  color: iv.textFaint,
                  fontWeight: 600,
                  mt: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {k.hint}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

/** Alt sayfalama çubuğu */
export function RiskPager({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const from = total === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(total, (safePage + 1) * pageSize);

  if (total <= pageSize) {
    return (
      <Box
        sx={{
          flexShrink: 0,
          px: 1.25,
          py: 0.55,
          borderTop: `1px solid ${iv.border}`,
          bgcolor: '#fafbfc'
        }}
      >
        <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
          {total ? `${total} kayıt` : 'Kayıt yok'}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        flexShrink: 0,
        px: 1.25,
        py: 0.45,
        borderTop: `1px solid ${iv.border}`,
        bgcolor: '#fafbfc',
        gap: 1
      }}
    >
      <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
        {from}–{to} / {total}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Button
          size="small"
          disabled={safePage <= 0}
          onClick={() => onChange(safePage - 1)}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 64, fontSize: 12 }}
        >
          Önceki
        </Button>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: iv.text, px: 0.5 }}>
          {safePage + 1} / {pageCount}
        </Typography>
        <Button
          size="small"
          disabled={safePage >= pageCount - 1}
          onClick={() => onChange(safePage + 1)}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 64, fontSize: 12 }}
        >
          Sonraki
        </Button>
      </Stack>
    </Stack>
  );
}

export function usePagedRows(rows, pageSize = RISK_PAGE_SIZE, resetKey) {
  const [page, setPage] = React.useState(0);
  const len = rows?.length || 0;
  React.useEffect(() => {
    setPage(0);
  }, [pageSize, resetKey, len]);
  const pageCount = Math.max(1, Math.ceil(len / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const slice = React.useMemo(() => {
    const list = rows || [];
    const start = safePage * pageSize;
    return list.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);
  return { page: safePage, setPage, slice, total: len, pageSize };
}
